import {
  APP_NAME,
  imageFields,
  metafieldFields,
  moneyFields,
  productFields,
  productVariantFields,
  variantFields,
} from "./fields";

import type IQueryBuilderOptions from "gql-query-builder/build/IQueryBuilderOptions";
import { tagMetafield } from "./metafields/collection-metafields";

export const cartCodeDiscountAllocationFragment = {
  operation: "CartCodeDiscountAllocation",
  fields: ["code", { discountedAmount: moneyFields }],
  fragment: true,
};

export const cartCustomDiscountAllocationFragment = {
  operation: "CartCustomDiscountAllocation",
  fields: ["title", { discountedAmount: moneyFields }],
  fragment: true,
};

export const cartAutomaticDiscountAllocationFragment = {
  operation: "CartAutomaticDiscountAllocation",
  fields: ["title", { discountedAmount: moneyFields }],
  fragment: true,
};

export const manualDiscountApplicationFragment = {
  operation: "ManualDiscountApplication",
  fields: ["title", "description"],
  fragment: true,
};

export const automaticDiscountApplicationFragment = {
  operation: "AutomaticDiscountApplication",
  fields: ["title"],
  fragment: true,
};

export const discountCodeApplicationFragment = {
  operation: "DiscountCodeApplication",
  fields: ["code", "applicable"],
  fragment: true,
};

export const brandNameMetafield = {
  operation: {
    name: 'metafield(namespace: "info", key: "brand_name")',
    alias: "brandName",
  },
  fields: metafieldFields,
  variables: {},
};

export const mediaImageFragment = {
  operation: "MediaImage",
  fields: ["id", "mediaContentType", { image: imageFields }],
  fragment: true,
};

export const videoFragment = {
  operation: "Video",
  fields: [
    "id",
    "alt",
    "mediaContentType",
    { sources: [{ operation: "VideoSource", fields: ["url", "format"], fragment: true }] },
  ],
  fragment: true,
};

export const videoImageFragment = {
  operation: "Video",
  fields: ["id", { sources: ["url", "width", "height", "format", "mimeType"] }, { previewImage: imageFields }],
  fragment: true,
};

export const collectionFragment = {
  operation: "Collection",
  fields: ["handle", "title", "id", tagMetafield],
  fragment: true,
};

const thumbnailImagesMetafield = {
  operation: {
    name: 'metafield(namespace: "operation", key: "thumbnail_images")',
    alias: "thumbnailImages",
  },
  fields: [
    ...metafieldFields,
    {
      operation: "references(first: 10)",
      fields: [{ edges: [{ node: [mediaImageFragment] }] }],
      variables: {},
    },
  ],
  variables: {},
};

// Products 상품 메타필드
const collectionProductMetafields: Record<string, IQueryBuilderOptions[]> = {
  tune: [],
  yeseyesee: [],
  thisisneverthat: [],
  khakis: [],
  sunlove: [],
  wellbeingexpress: [thumbnailImagesMetafield],
};

export const productFragment = {
  operation: "Product",
  fields: [...productFields, ...collectionProductMetafields[APP_NAME]],
  fragment: true,
};

export const productVariantFragment = {
  operation: "ProductVariant",
  fields: productVariantFields,
  fragment: true,
};

export const moneyFragment = {
  operation: "MoneyV2",
  fields: ["amount"],
  fragment: true,
};

export const percentageFragment = {
  operation: "PricingPercentageValue",
  fields: ["percentage"],
  fragment: true,
};

export const metaObjectImageFragment = {
  operation: "Metaobject",
  fields: ["id", { fields: ["key", "value", "type", { reference: [mediaImageFragment] }] }],
  fragment: true,
};

const metaObjectReferencesFragment = {
  operation: "references(first: 100)",
  fields: [{ edges: [{ node: [productFragment, mediaImageFragment] }] }],
  variables: {},
};

const metaObjectReferences: Record<string, IQueryBuilderOptions[]> = {
  tune: [],
  yeseyesee: [],
  thisisneverthat: [],
  khakis: [],
  sunlove: [],
  wellbeingexpress: [metaObjectReferencesFragment],
};

export const metaObjectFragment = {
  operation: "Metaobject",
  fields: [
    "id",
    "type",
    {
      fields: [
        "key",
        "value",
        "type",
        {
          operation: "reference",
          fields: [mediaImageFragment, videoFragment, productFragment],
          variables: {},
        },
        ...metaObjectReferences[APP_NAME],
      ],
    },
  ],
  fragment: true,
};

// Khakis 성수 재고 메타필드 프래그먼트
export const seongsuVariantStockMetafield = {
  operation: {
    name: 'metafield(namespace: "custom", key: "seongsu_stock")',
    alias: "seongsuStock",
  },
  fields: metafieldFields,
  variables: {},
};

// Khakis 연희 재고 메타필드 프래그먼트
export const yeonhuiVariantStockMetafield = {
  operation: {
    name: 'metafield(namespace: "custom", key: "yeonhui_stock")',
    alias: "yeonhuiStock",
  },
  fields: metafieldFields,
  variables: {},
};

export const variantMetafields: Record<string, IQueryBuilderOptions[]> = {
  tune: [],
  yeseyesee: [],
  thisisneverthat: [],
  khakis: [seongsuVariantStockMetafield, yeonhuiVariantStockMetafield],
  sunlove: [],
  wellbeingexpress: [],
};

const hiddenName: Record<string, string> = {
  tune: '(namespace: "custom", key: "hidden")',
  yeseyesee: '(namespace: "custom", key: "hidden")',
  thisisneverthat: '(namespace: "operation", key: "hidden")',
  khakis: '(namespace: "operation", key: "hidden")',
  sunlove: '(namespace: "operation", key: "hidden")',
  wellbeingexpress: '(namespace: "operation", key: "hidden")',
};

export const hiddenMetafield = {
  operation: {
    name: `metafield${hiddenName[APP_NAME]}`,
    alias: "hidden",
  },
  fields: metafieldFields,
  variables: {},
};

export const metaObjectProductFragment = {
  operation: "Metaobject",
  fields: [
    "id",
    {
      fields: [
        "key",
        "value",
        {
          operation: "references(first: 120)",
          fields: [
            {
              edges: [
                {
                  node: [
                    {
                      operation: "Product",
                      fields: [
                        ...productFields,
                        brandNameMetafield,
                        hiddenMetafield,
                        {
                          operation: "variants(first: 40)",
                          fields: [{ edges: [{ node: [...productVariantFields] }] }],
                          variables: {},
                        },
                      ],
                      fragment: true,
                    },
                  ],
                },
              ],
            },
          ],
          variables: {},
        },
      ],
    },
  ],
  fragment: true,
};

export const metaObjectImageProductFragment = {
  operation: "Metaobject",
  fields: [
    "id",
    {
      fields: [
        "key",
        "value",
        { reference: [mediaImageFragment, productFragment, videoFragment] },
        {
          operation: "references(first: 12)",
          fields: [
            {
              edges: [
                {
                  node: [
                    {
                      operation: "Product",
                      fields: [
                        ...productFields,
                        {
                          operation: { name: 'metafield(namespace: "info", key: "tag")', alias: "tag" },
                          fields: metafieldFields,
                          variables: {},
                        },
                        {
                          operation: {
                            name: 'metafield(namespace: "custom", key: "launch")',
                            alias: "launches",
                          },
                          fields: [
                            ...metafieldFields,
                            {
                              reference: [
                                {
                                  operation: "Metaobject",
                                  fields: [
                                    "id",
                                    {
                                      fields: ["key", "value", "type"],
                                    },
                                  ],
                                  fragment: true,
                                },
                              ],
                            },
                          ],
                          variables: {},
                        },
                        {
                          operation: "variants(first: 10)",
                          fields: [
                            {
                              edges: [
                                {
                                  node: [...variantFields, { product: ["handle"] }, ...variantMetafields[APP_NAME]],
                                },
                              ],
                            },
                          ],
                          variables: {},
                        },
                      ],
                      fragment: true,
                    },
                    {
                      operation: "MediaImage",
                      fields: ["id", mediaImageFragment],
                      fragment: true,
                    },
                  ],
                },
              ],
            },
          ],
          variables: {},
        },
      ],
    },
  ],
  fragment: true,
};
